import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Typography,
  Tooltip,
} from "antd";
import { Match } from "../../Lib/types";
import { FC } from "react";
import { PlusOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useForm, useModal, useTable } from "@refinedev/antd";
import { errorNotification, successNotification } from "../../../../helpers";
import { MatchesMediaCard } from "./Matches-Media-Card";
import { AppImageUploader, EFileTypes } from "../../../../components";
import { v4 as randomId } from "uuid";
import {
  arrayLengthValidator,
  noSpecialCharValidator,
} from "../../../../validators";
import { extractMediaUrl } from "../helpers/vk-video";

const { Title, Paragraph } = Typography;

const ANNOUNCE_MEDIA_INPUT_TYPE = {
  LINK_URL: "LINK_URL",
  LINK_IDS: "LINK_IDS",
} as const;

type AnnounceMediaInputType =
  (typeof ANNOUNCE_MEDIA_INPUT_TYPE)[keyof typeof ANNOUNCE_MEDIA_INPUT_TYPE];

const announceMediaTypeSelect: {
  key: AnnounceMediaInputType;
  value: string;
}[] = [
  { key: ANNOUNCE_MEDIA_INPUT_TYPE.LINK_URL, value: "Ссылка" },
  { key: ANNOUNCE_MEDIA_INPUT_TYPE.LINK_IDS, value: "VK ID" },
];

type MatchesAnnounceProps = Pick<Match, "media"> & {
  onRefetchCard: () => void;
  matchId: number;
  leagueId: number;
  fnlId?: Match["fnlId"];
};

export const MatchesAnnounce: FC<MatchesAnnounceProps> = ({
  media,
  onRefetchCard,
  matchId,
  leagueId,
  fnlId,
}) => {
  const { modalProps, show, close } = useModal();

  const { formProps, saveButtonProps, form, formLoading } = useForm<{
    mediaInputType?: AnnounceMediaInputType;
    url?: string | null;
    videoUrl?: string | null;
    vidOid?: number | null;
    vidId?: number | null;
    title?: string;
    hashTags?: string[];
  }>({
    action: "create",
    resource: "matchesMedia",
    meta: {
      matchId,
      leagueId,
      fnlId,
      type: "ANNOUNCE_REVIEW",
    },
    dataProviderName: "matchesMediaDataProvider",
    redirect: false,
    ...successNotification({
      successData: {
        message: "Анонс добавлен",
        type: "success",
      },
      callback: () => {
        close();
        onRefetchCard();
        form.resetFields();
      },
    }),
    ...errorNotification({}),
  });

  const { tableProps, searchFormProps, setFilters } = useTable<any>({
    dataProviderName: "hashTagsDataProvider",
    resource: "hashtags",
    syncWithLocation: false,
    queryOptions: {
      enabled: true,
    },
  });

  const hashTags = tableProps.dataSource;

  const selectOptions = hashTags?.map((value) => {
    return { value, label: value };
  });

  const MAX_ALBUM_PHOTO_COUNT = 50;
  const watchType = Form.useWatch("type", formProps.form);
  const watchLength = Form.useWatch("photos", formProps.form);
  const stopAdding = (watchLength?.length ?? 0) < MAX_ALBUM_PHOTO_COUNT;
  const handleChangeType = () => {
    form.setFieldValue("url", null);
    form.setFieldValue("videoUrl", null);
  };

  const handleChangeInputType = () => {
    form.setFieldValue("videoUrl", null);
    form.setFieldValue("vidOid", null);
    form.setFieldValue("vidId", null);
  };

  const afterClose = () => {
    form.resetFields();
  };

  const watchInputType = Form.useWatch("mediaInputType", formProps.form);

  const handleFinish = async (rawValues: unknown) => {
    const values = rawValues as {
      mediaInputType?: AnnounceMediaInputType;
      videoUrl?: string | null;
      vidOid?: number | null;
      vidId?: number | null;
      [key: string]: unknown;
    };

    const { mediaInputType, videoUrl, vidOid, vidId, ...rest } = values;

    const directVideoUrl =
      mediaInputType === ANNOUNCE_MEDIA_INPUT_TYPE.LINK_URL
        ? extractMediaUrl(videoUrl)
        : undefined;

    return await formProps.onFinish?.({
      ...rest,
      ...(directVideoUrl ? { videoUrl: directVideoUrl } : {}),
      ...(mediaInputType === ANNOUNCE_MEDIA_INPUT_TYPE.LINK_IDS
        ? {
            vidOid:
              vidOid !== null && vidOid !== undefined
                ? String(Math.abs(Number(vidOid)))
                : undefined,
            vidId:
              vidId !== null && vidId !== undefined
                ? String(Number(vidId))
                : undefined,
            videoUrl: null,
          }
        : {}),
    });
  };

  return (
    <>
      <Space direction={"vertical"} size={16} style={{ width: "100%" }}>
        <Space
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Title level={5}>Анонс обзора</Title>
          {Boolean(media?.length) && (
            <Button onClick={show} icon={<PlusOutlined />}>
              Добавить анонс обзора
            </Button>
          )}
        </Space>
        {!media?.filter((content) => content.type === "ANNOUNCE_REVIEW")
          .length ? (
          <Card
            style={{
              textAlign: "center",
              display: "flex",
              height: 200,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Space direction={"vertical"} size={16}>
              <Title level={5}>Анонс обзора отсутствует</Title>
              <Button onClick={show} icon={<PlusOutlined />}>
                Добавить анонс обзора
              </Button>
            </Space>
          </Card>
        ) : (
          <Row gutter={[8, 8]}>
            {media
              ?.filter((content) => content.type === "ANNOUNCE_REVIEW")
              .map((content, index) => (
                <MatchesMediaCard
                  selectOptions={selectOptions}
                  key={content.id}
                  media={content}
                  count={index}
                  onRefetchCard={onRefetchCard}
                  fnlId={fnlId}
                />
              ))}
          </Row>
        )}
      </Space>

      <Modal
        title={
          <div>
            Добавление анонса обзора{" "}
            <Tooltip
              title="Анонс обзора автоматически добавляются в медиацентр.
              В веб версии отображаются первое видео и фото первого альбома"
            >
              <InfoCircleOutlined />
            </Tooltip>
          </div>
        }
        onCancel={close}
        afterClose={afterClose}
        {...modalProps}
        footer={<></>}
      >
        <Form
          style={{
            width: "100%",
          }}
          {...formProps}
          onFinish={handleFinish}
          initialValues={{
            type: "ANNOUNCE_REVIEW",
            typeLink: "review",
            photos: [],
            url: null,
            videoUrl: null,
            mediaInputType: ANNOUNCE_MEDIA_INPUT_TYPE.LINK_URL,
            vidOid: null,
            vidId: null,
          }}
          layout={"vertical"}
        >
          <Form.Item name={"typeLink"} hidden>
            <Input />
          </Form.Item>
          <>
            <Form.Item
              label={
                <div>
                  Превью {/*  */}
                  <Tooltip title="Максимальный размер фото 5Мб.">
                    <InfoCircleOutlined />
                  </Tooltip>
                </div>
              }
              name={"url"}
              rules={[
                {
                  required: true,
                  message: "Необходимо прикрепить превью",
                },
              ]}
            >
              <AppImageUploader
                type="MATCH"
                entityId={`${matchId}/${randomId()}`}
                serviceType={EFileTypes.MATCHES}
                cropperProps={{
                  modalTitle: "Редактировать изображение",
                }}
                uploadProps={{
                  multiple: false,
                  children: "Прикрепите фото",
                  listType: "picture-card",
                }}
              />
            </Form.Item>
          </>

          <Form.Item name={"mediaInputType"} label={"Тип медиа"}>
            <Select
              onChange={handleChangeInputType}
              placeholder="Выберите тип медиа"
              notFoundContent={"Тип не найден"}
            >
              {announceMediaTypeSelect.map((translation) => {
                return (
                  <Select.Option key={translation.key} value={translation.key}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      {translation.value}
                    </div>
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          {watchInputType === ANNOUNCE_MEDIA_INPUT_TYPE.LINK_URL && (
            <Form.Item
              name={"videoUrl"}
              label={
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  Ссылка медиа{" "}
                  <Tooltip
                    title={
                      <>
                        {`1. Добавление ссылки на ВК.
                      Для добавления ссылки необходимо авторизироваться в ВК
                      Ссылка добавляется на ролик через кнопку Share – Export
                      Общий формат ссылки в вк <iframe src="https://vk.com/video_ext.php?oid=-29484355&id=456247241&hd=2" width="853" height="480" allow="autoplay; encrypted-media; fullscreen; picture-in-picture;" frameborder="0" allowfullscreen></iframe>
                      Добавляется только выделенная часть https://vk.com/video_ext.php?oid=-29484355&id=456247241&hd=2 .`}
                        <br />
                        {
                          "2.Добавление ссылки на Youtube. Должна начинаться с (https://) youtu.be// или  (https://) www.youtube.com"
                        }
                      </>
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </div>
              }
              rules={[
                {
                  required: true,
                  message: "Необходимо указать ссылку",
                },
                {
                  validator: async (_: unknown, value?: string) => {
                    const mediaUrl = extractMediaUrl(value);

                    if (!mediaUrl) return;

                    try {
                      new URL(mediaUrl);
                    } catch {
                      throw new Error("Укажите корректную ссылку");
                    }
                  },
                },
              ]}
            >
              <Input placeholder={"Укажите ссылку"} />
            </Form.Item>
          )}

          {watchInputType === ANNOUNCE_MEDIA_INPUT_TYPE.LINK_IDS && (
            <>
              <Form.Item
                name={"vidOid"}
                label={"OID"}
                rules={[
                  {
                    required: true,
                    message: "Укажите OID",
                  },
                ]}
              >
                <InputNumber
                  precision={0}
                  controls={false}
                  placeholder={"Например 29484355"}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                name={"vidId"}
                label={"ID"}
                rules={[
                  {
                    required: true,
                    message: "Укажите ID",
                  },
                ]}
              >
                <InputNumber
                  min={1}
                  precision={0}
                  controls={false}
                  placeholder={"Например 456247241"}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </>
          )}

          <Form.Item
            name={"title"}
            label={
              <div>
                Заголовок{" "}
                <Tooltip
                  title="Не более 100 символов и не менее 5 символов. Поле не должно
              содержать специальных символов."
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
            }
            rules={[
              {
                required: true,
                message: "Необходимо указать заголовок",
              },
              {
                max: 100,
                min: 5,
                message: "Не более 100 символов и не менее 5 символов",
              },
              noSpecialCharValidator,
            ]}
          >
            <Input placeholder={"Укажите заголовок"}></Input>
          </Form.Item>
          <Form.Item
            name={"hashTags"}
            label={"Хэштеги"}
            rules={[arrayLengthValidator(3, "Хэштегов не может быть больше 3")]}
          >
            <Select
              placeholder={"Укажите теги"}
              showSearch
              mode={"multiple"}
              options={selectOptions}
              notFoundContent={
                <div
                  style={{
                    width: "100%",
                    height: 100,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Укажите хэштеги
                </div>
              }
            />
          </Form.Item>
          <Button type={"primary"} htmlType={"submit"}>
            Добавить медиа
          </Button>
        </Form>
      </Modal>
    </>
  );
};
